import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { getToken } from "Session";
import { makeWebSocketEvent } from "utils/web-socket-events";
import { AttendanceChatProps, ChatControlsContext } from "./types";

import { useAttendanceContext } from "contexts/attendance-context";
import { useInitWebSocket } from "components/atendimentos-components/use-init-web-socket";
import { useRouter } from "routes/router-control/use-router";
import { Attendance } from "components/atendimentos-components/atendimento-form/types";

const chatsContext = createContext<ChatControlsContext>({
  sendWsMessage: () => {},
  connectStatus: "CONNECTING",
  selectedAttendance: undefined,
  setSelectedAttendance: () => undefined,
  attendancesMap: new Map(),
  setAttendanceMap: () => undefined,
  setAttendance: () => undefined,
});

const url = process.env.REACT_APP_PUBLIC_WEB_SOCKET!;

// Keys que vão ser automaticamente atualizadas caso o atendimento seja alterado
export const autoUpdateKeys: { rowKey: string; attendanceKey: string }[] = [
  { rowKey: "nome", attendanceKey: "nome" },
  {
    rowKey: "telefoneContatoNormalizado",
    attendanceKey: "telefoneContatoNormalizado",
  },
  { rowKey: "etapa", attendanceKey: "etapa" },
  { rowKey: "convenio", attendanceKey: "convenio" },
  { rowKey: "tabulacao", attendanceKey: "tabulacao" },
  { rowKey: "userId", attendanceKey: "user.id" },
  { rowKey: "usuarioNome", attendanceKey: "user.name" },
];

export const ChatsContextProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [attendancesMap, setAttendanceMap] = useState<
    Map<number, AttendanceChatProps>
  >(new Map());
  const [selectedAttendance, setSelectedAttendance] =
    useState<AttendanceChatProps>();
  const connectHeadersTelaChats = { token: getToken(), client: "tela-chats" };
  const isConnect = true;
  const { formControl, dispatch } = useAttendanceContext();
  const chatControlsApi = useInitWebSocket({
    headers: connectHeadersTelaChats,
    isConnect,
    onMessage: ({ data }) => {
      const parsedData = JSON.parse(data);
      // Temporário
      parsedData.responseType = "atendimento";
      //
      const { responseType } = parsedData;
      if (responseType) {
        makeWebSocketEvent(responseType, parsedData);
      }
    },
    url,
  });
  const router = useRouter();

  const connectStatus = {
    "-1": "UNINSTANTIATED",
    "0": "CONNECTING",
    "1": "CONNECTED",
    "2": "DISCONNECTING",
    "3": "DISCONNECTED",
  }[chatControlsApi.readyState] as ChatControlsContext["connectStatus"];

  const sendWsMessage = (data: any) => {
    chatControlsApi.sendJsonMessage(data);
  };

  // Quando o atendimento é alterado, será sincronizado algumas propriedades com selectedAttendance
  useEffect(() => {
    if (
      formControl.values.id &&
      router.pathname === "/atendimentos" &&
      selectedAttendance?.id
    ) {
      autoUpdateKeys.forEach(({ rowKey, attendanceKey }) => {
        const atendimento = formControl.initialValues as any;
        const selectedAttendanceProp = rowKey
          .split(".")
          .reduce((acc, crrKey) => {
            acc = acc[crrKey];
            return acc;
          }, selectedAttendance as any);
        const currentAttendanceProp = attendanceKey
          .split(".")
          .reduce((acc, crrKey) => {
            acc = acc[crrKey];
            return acc;
          }, atendimento);

        if (currentAttendanceProp !== selectedAttendanceProp) {
          setSelectedAttendance((prev) => {
            return { ...(prev as any), [rowKey]: currentAttendanceProp };
          });
        }
      });
    }
  }, [formControl]);

  // SETA UM ATENDIMENTO ESPECÍFICO.
  const setAttendance = useCallback((data: AttendanceChatProps) => {
    setAttendanceMap((prevMap) => {
      prevMap.set(data.id, data);
      return new Map(prevMap.entries());
    });
  }, []);

  // Serve para manter sincronizado a linha da listagem com o state da linha selecionada
  useEffect(() => {
    if (selectedAttendance) {
      let updatedRow = attendancesMap.get(selectedAttendance.id);
      if (updatedRow) {
        autoUpdateKeys.forEach(({ rowKey, attendanceKey }) => {
          // const propSelectedAttendance =
          //   selectedAttendance[rowKey as keyof AttendanceChatProps];
          // const propAttendance =
          //   formControl.values[attendanceKey as keyof Attendance];
          (updatedRow as any)[rowKey as keyof AttendanceChatProps] =
            selectedAttendance[rowKey as keyof AttendanceChatProps];
          // if (propAttendance !== propSelectedAttendance) {
          //   dispatch({
          //     type: "changeField",
          //     payload: {
          //       fieldName: attendanceKey as keyof Attendance,
          //       data: propSelectedAttendance,
          //     },
          //   });
          // }
        });
        setAttendance(updatedRow);
      }
    }
  }, [selectedAttendance]);

  return (
    <chatsContext.Provider
      value={{
        sendWsMessage,
        connectStatus,
        selectedAttendance,
        setSelectedAttendance,
        attendancesMap,
        setAttendanceMap,
        setAttendance,
      }}
    >
      {children}
    </chatsContext.Provider>
  );
};

export const useChatsContext = () => {
  const context = useContext(chatsContext);
  if (context === undefined)
    throw new Error(
      "useApplicationContext must be used within a ApplicationContextProvider"
    );
  return context;
};
